import React, { useState, useEffect } from "react";
import { Box, Grid, Divider } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useTranslate } from "src/locales";

interface ChoicesGridUpdateProps {
    open: boolean;
    onClose: () => void;
    data: any;
    onSave: (updatedData: any) => void;
}

const ChoicesGridUpdate: React.FC<ChoicesGridUpdateProps> = ({
    open,
    onClose,
    data,
    onSave,
}) => {
    const { t } = useTranslate();
    const [formData, setFormData] = useState({
        amount: "",
        unit: "",
        doctor: "",
        direction: "",
    });

    useEffect(() => {
        if (data && open) {
            setFormData({
                amount: data.amount?.toString() || "",
                unit: data.unit || "",
                doctor: data.doctor || "",
                direction: data.direction || "",
            });
        }
    }, [data, open]);

    const handleSave = () => {
        onSave({ ...data, ...formData });
        onClose();
    };

    return (
        <OpenComponentDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            hideHeader
            showPatientData={false}
        >
            <Box
                sx={{
                    py: { xs: 2, sm: 3 },
                    px: { xs: 1.5, sm: 2 },
                    width: "100%",
                }}
            >
                {/* Stok Adı Alanı - "Değiştir" başlığı ile aynı tasarım */}
                <Box
                    sx={{
                        backgroundColor: "#F4F7FE",
                        borderBottom: "1px solid #E0E0E0",
                        mb: 2,
                        px: 3,
                        py: 1,
                    }}
                >
                    <ByzMuiTypography isTitle={true}>
                        {data?.name || ""}
                    </ByzMuiTypography>
                </Box>

                {/* Patient Data */}
                <Box sx={{ mb: 3 }}>
                    <PatientDatas
                        patientId={data?.ptId || data?.patientId}
                        isCreateMedCard
                        noMargin
                    />
                </Box>

                {/* Save / Close Butonları - Patient Data'nın altında */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                        gap: 1,
                        mt: 1,
                    }}
                >
                    <ByzMuiSaveButton onClick={handleSave} />
                    <Box sx={{ flex: 1 }} />
                    <ByzMuiCloseButton onClick={onClose} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Form Alanları */}
                <Box sx={{ mt: 1, width: "100%" }}>
                    <Box
                        sx={{
                            backgroundColor: "#F4F7FE",
                            borderBottom: "1px solid #E0E0E0",
                            mb: 1.5,
                            px: 3,
                            py: 1,
                        }}
                    >
                        <ByzMuiTypography isTitle={true}>
                            {"Değiştir"}
                        </ByzMuiTypography>
                    </Box>

                    <Grid container spacing={0}>
                        {/* Miktar */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {"Miktar"}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzSelectListPopupInput
                                        label={"Seçiniz"}
                                        value={formData.amount}
                                        onChange={(val: any) => {
                                            const item = Array.isArray(val) ? val[0] : val;
                                            setFormData(prev => ({ ...prev, amount: item?.NAME || item?.name || val }));
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Birimi */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {"Birimi"}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzSelectListPopupInput
                                        label={"Seçiniz"}
                                        value={formData.unit}
                                        onChange={(val: any) => {
                                            const item = Array.isArray(val) ? val[0] : val;
                                            setFormData(prev => ({ ...prev, unit: item?.NAME || item?.name || val }));
                                        }}
                                        url="/reference/api/StockUnits/field-units"
                                        displayField="name"
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Doktor */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {"Doktor"}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzSelectListPopupInput
                                        label={"Seçiniz"}
                                        value={formData.doctor}
                                        onChange={(val: any) => {
                                            const item = Array.isArray(val) ? val[0] : val;
                                            setFormData(prev => ({ ...prev, doctor: item?.NAME || item?.name || val }));
                                        }}
                                        url="/reference/api/Users/field-doctors"
                                        displayField="name"
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Yön */}
                        <Grid item xs={12} sx={{ py: 1 }}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item xs={12} md={4} sx={{ borderBottom: "1px solid #E0E0E0", py: 1 }}>
                                    <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                                        {"Yön"}
                                    </ByzMuiTypography>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <ByzMuiTextField
                                        label={"Giriniz"}
                                        value={formData.direction}
                                        onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                                        fullWidth
                                        noPlaceholder
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </OpenComponentDialog>
    );
};

export default ChoicesGridUpdate;
